import { maintenanceFetch, maintenanceGate } from '../maintenance/maintenance-transport';
import { MaintenanceService } from '../maintenance/maintenance.service';
import { isPlatformBrowser } from '@angular/common';
import { ApplicationConfig, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { ApolloLink, FetchResult, InMemoryCache, Observable, createHttpLink, split } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { APOLLO_FLAGS, APOLLO_OPTIONS, Apollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { LocalStorageWrapper, persistCache } from 'apollo3-cache-persist';
import { Kind, OperationTypeNode } from 'graphql';
import { createClient } from 'graphql-ws';
import { environment } from 'src/environments/environment';
import { getAccessToken, setAccessToken } from '../entry/auth/login/auth.utils';
import { SessionService } from '../entry/session.service';
import { StorageService } from '../services/storage.service';
import { ErrorCode } from '../shared/constants';
import { NotificationService } from '../services/notification.service';

// const apollo: Apollo;
// const uri = 'http://localhost:3000/graphql'; // <-- add the URL of the GraphQL server here
const apiOrigin = environment.apiOrigin;

function isErrorCode(value: unknown): value is ErrorCode {
  return Object.values(ErrorCode).includes(value as ErrorCode);
}

function handleForbiddenError(err: unknown, toast: NotificationService): void {
  if (isErrorCode(err) && err === ErrorCode.FORBIDDEN_EXCEPTION) {
    toast.show(
      $localize`:@@errorForbidden:Forbidden: You do not have permission to access this resource.`,
      $localize`:@@close:Close`,
      5000
    );
  }
}

function clearSessionAndRedirect(
  storageService: StorageService,
  sessionService: SessionService,
  routerRef: Router
): void {
  setAccessToken(storageService, '');
  sessionService.isAuthenticated$.next(false);
  routerRef.navigate(['login']);
}

function handleUnAuthError(
  storageService: StorageService,
  sessionService: SessionService,
  operation: any,
  forward: any,
  routerRef: Router
): Observable<FetchResult> | undefined {
  clearSessionAndRedirect(storageService, sessionService, routerRef);
  return undefined;
}

function handleTokenError(
  storageService: StorageService,
  sessionService: SessionService,
  err: unknown,
  routerRef: Router
): void {
  // TOKEN_IS_EXPIRED is handled by the refresh logic in the error link; only clear + redirect for missing token.
  if (isErrorCode(err) && err === ErrorCode.JWT_MUST_BE_PROVIDED) {
    clearSessionAndRedirect(storageService, sessionService, routerRef);
  }
}

/**
 * Creates and configures an Apollo client instance for Angular applications.
 *
 * This function sets up HTTP and WebSocket links for GraphQL operations, handles authentication,
 * error handling, and cache persistence. It supports different configurations for local development
 * and production environments, and manages token-based authentication for both HTTP and WebSocket connections.
 *
 * @param httpLink - The Angular HttpLink service for creating HTTP connections.
 * @param snackBar - The Angular Material MatSnackBar service for displaying notifications.
 * @param sessionService - Service responsible for refreshing authentication tokens.
 * @param storageService - Service for accessing and managing local storage (e.g., tokens).
 * @param platformId - The current platform identifier (used to determine browser/server context).
 * @returns An Apollo client configuration object with custom links, cache, and default options.
 */
export function createApollo(
  httpLink: HttpLink,
  toast: NotificationService,
  routerRef: Router,
  storageService: StorageService,
  sessionService: SessionService,
  platformId: string,
  maintenance: MaintenanceService
): any {
  const authLink = setContext((operation, context) => {
    const token = getAccessToken(storageService);

    if (token) {
      return {
        headers: {
          ...context.headers,
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };
    }

    return context;
  });

  // HTTP connection to the API
  // For local development, use a different configuration when connecting to localhost
  const isLocalDevelopment = apiOrigin.includes('127.0.0.1') || apiOrigin.includes('localhost');
  const isBrowser = isPlatformBrowser(platformId);
  const isPageSecure = isBrowser && typeof window !== 'undefined' && window.location.protocol === 'https:';
  const useLocalProxy = isLocalDevelopment && isBrowser;
  // When the page is served over HTTPS we must talk to the API over HTTPS/WSS too, even in local dev,
  // otherwise the browser blocks the requests as mixed content.
  const useSecureTransport = isPageSecure || !isLocalDevelopment;
  const pageHost = isBrowser && typeof window !== 'undefined' ? window.location.host : '';

  const graphqlUrl = useLocalProxy ? '/graphql' : `${useSecureTransport ? 'https' : 'http'}://${apiOrigin}/graphql`;

  const guardedFetch = maintenanceFetch(maintenance);
  const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
    if (maintenance.blocked()) {
      return undefined;
    }
    if (networkError) {
      if (maintenance.blocked()) {
        return undefined;
      }
      maintenance.reportFailure((networkError as { statusCode?: number }).statusCode ?? 0, null);
      return undefined; // The shared connection widget owns transport failures.
    }
    if (graphQLErrors) {
      for (const err of graphQLErrors) {
        const rawCode = err.extensions?.errorCode ?? err.extensions?.code;
        const errorCode = isErrorCode(rawCode) ? rawCode : null;
        if (errorCode === ErrorCode.UNAUTHORIZED) {
          return handleUnAuthError(storageService, sessionService, operation, forward, routerRef);
        }
        if (isErrorCode(errorCode) && errorCode === ErrorCode.TOKEN_IS_EXPIRED) {
          // Attempt to refresh the access token using the refresh token cookie, then retry.
          return new Observable<FetchResult>((observer) => {
            guardedFetch(graphqlUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ query: 'mutation { refreshAccessToken }' })
            })
              .then((res) => {
                if (!res.ok) {
                  throw new Error('API unavailable');
                }
                return res.json();
              })
              .then((json) => {
                if (maintenance.blocked()) {
                  observer.error(new Error('MAINTENANCE'));
                  return;
                }
                const newToken: string | null = json?.data?.refreshAccessToken ?? null;
                if (newToken) {
                  setAccessToken(storageService, newToken);
                  // Retry the original operation with the new token in the header.
                  const subscriber = {
                    next: observer.next.bind(observer),
                    error: observer.error.bind(observer),
                    complete: observer.complete.bind(observer)
                  };
                  if (maintenance.blocked()) {
                    observer.error(new Error('MAINTENANCE'));
                    return;
                  }
                  forward(operation).subscribe(subscriber);
                } else {
                  clearSessionAndRedirect(storageService, sessionService, routerRef);
                  observer.complete();
                }
              })
              .catch((error: unknown) => {
                observer.error(error);
              });
          });
        }
        // Only handle generic errors here
        handleForbiddenError(err.message, toast);
        handleTokenError(storageService, sessionService, errorCode, routerRef);
      }
    }
    // Only forward (retry) queries — never retry mutations to avoid duplicate side-effects
    const isMutation = operation.query.definitions.some((d: any) => d.operation === 'mutation');
    return isMutation ? undefined : forward(operation);
  });

  const http = createHttpLink({
    // For production you should use an absolute URL here
    uri: graphqlUrl,
    credentials: 'include',
    fetch: guardedFetch
  });

  // Create the subscription websocket link
  // ws: for regular and wss: for secured
  // localhost:3000 - same as http because of GraphQL module option installSubscriptionHandlers
  const wsLink = isBrowser
    ? new GraphQLWsLink(
        createClient({
          url: useLocalProxy
            ? `${isPageSecure ? 'wss' : 'ws'}://${pageHost}/graphql`
            : `${useSecureTransport ? 'wss' : 'ws'}://${apiOrigin}/graphql`,
          lazy: true,
          connectionParams: () => {
            const token = getAccessToken(storageService);
            return {
              Authorization: token ? `Bearer ${token}` : ''
            };
          },
          on: {
            connected: () => undefined,
            closed: () => undefined
          }
        })
      )
    : null;

  const linkSplit =
    wsLink != null
      ? split(
          ({ query }) => {
            const definition = getMainDefinition(query);
            return (
              definition.kind === Kind.OPERATION_DEFINITION && definition.operation === OperationTypeNode.SUBSCRIPTION
            );
          },
          wsLink, // Send subscription traffic to websocket link
          http // All other traffic to http link
        )
      : http;

  const link = ApolloLink.from([maintenanceGate(maintenance), errorLink, authLink, linkSplit]);

  const cache = new InMemoryCache();

  if (isPlatformBrowser(platformId)) {
    persistCache({
      cache,
      storage: new LocalStorageWrapper(window.localStorage),
      key: `apollo-cache-${environment.cacheVersion}`
    });
  }

  return {
    link,
    cache,
    connectToDevTools: environment.production ? false : true,
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network'
      },
      query: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'all'
      },
      mutate: {
        fetchPolicy: 'no-cache'
      },
      subscription: {
        fetchPolicy: 'no-cache'
      }
    }
  };
}

export const graphqlProvider: ApplicationConfig['providers'] = [
  Apollo,
  {
    provide: APOLLO_FLAGS,
    useValue: {
      useInitialLoading: true // enable it here
    }
  },
  {
    provide: APOLLO_OPTIONS,
    useFactory: createApollo,
    deps: [HttpLink, NotificationService, Router, StorageService, SessionService, PLATFORM_ID, MaintenanceService]
  }
];
